import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AppIcon from "../../Core/AppIcon/AppIcon";
import AppInputNew from "../../Core/AppInput/AppInputNew";
import { CreateButton } from "../../Core/Button/Button";
import Tab from "../Tabs/Tab";
import { ListFilters } from "../Filters/useListFilters";
import ColumnSettingsMenu from "../ListColumns/ColumnSettingsMenu";
import { ListColumns } from "../ListColumns/useListColumns";
import ListActionsMenu, { ListToolbarAction } from "./ListActionsMenu";

export type { ListToolbarAction } from "./ListActionsMenu";

interface ListPageToolbarProps<TDef, TRow> {
	/** The shared filter state for this page (from `useListFilters`). */
	filters: ListFilters<TDef>;
	/** The result count shown in the active-filter badge (rows.length / totalCount). */
	count: number;
	/** Opens the filter drawer. */
	onOpenFilterPanel: () => void;
	/** Route the "create" button links to. */
	createTo: string;
	/** Translation key for the "create" button label. */
	createLabel: string;
	/**
	 * Optional column-visibility state (from `useListColumns`). When provided, a
	 * column-picker button is shown left of the filter button.
	 */
	columns?: ListColumns<TRow>;
	/**
	 * Optional overflow actions. When provided, a "..." button (matching the
	 * filter button) is shown to the left of it, opening a dropdown of these.
	 */
	menuActions?: ListToolbarAction[];
}

/**
 * The toolbar above a virtualized list page: free-text search, the
 * organisation + personal saved-filter pills, the filter-drawer button (with an
 * active count + clear when a custom filter is applied) and the create button.
 *
 * Driven entirely by a {@link ListFilters} object, so jobs, customers and
 * consultants share one toolbar — each page only supplies its result count,
 * create route/label and the drawer opener.
 */
function ListPageToolbar<TDef, TRow = unknown>({
	filters,
	count,
	onOpenFilterPanel,
	createTo,
	createLabel,
	columns,
	menuActions,
}: ListPageToolbarProps<TDef, TRow>) {
	const { t } = useTranslation();
	const {
		searchTerm,
		setSearchTerm,
		orgFilters,
		savedFilters,
		applyDefinition,
		selectSavedFilter,
		isOrgFilterActive,
		isSavedFilterActive,
		hasActiveFilter,
		clearFilter,
	} = filters;

	// Fade the pill row's edges only where there is actually more to scroll to,
	// so a toolbar whose pills all fit shows no gradient at all.
	const pillScrollRef = useRef<HTMLDivElement>(null);
	const [fade, setFade] = useState({ start: false, end: false });

	const updateFade = useCallback(() => {
		const el = pillScrollRef.current;
		if (!el) return;
		// 1px slack — fractional layout widths otherwise leave a fade pinned on
		// at either end
		const maxScroll = el.scrollWidth - el.clientWidth;
		const next = { start: el.scrollLeft > 1, end: maxScroll > 1 && el.scrollLeft < maxScroll - 1 };
		setFade((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
	}, []);

	useEffect(() => {
		const el = pillScrollRef.current;
		if (!el) return;
		updateFade();
		if (typeof ResizeObserver === "undefined") return;
		// The container resizes with the window; the pills themselves resize when
		// their labels land (translations, saved-filter names)
		const observer = new ResizeObserver(updateFade);
		observer.observe(el);
		Array.from(el.children).forEach((child) => observer.observe(child));
		return () => observer.disconnect();
	}, [updateFade, orgFilters, savedFilters]);

	return (
		<div className="px-4 pt-4">
			<div className="flex items-center gap-5 mb-3">
				<div className="w-[240px] shrink-0">
					<AppInputNew
						containerClassName="[&>p:last-child]:hidden"
						placeholder={t("SEARCH")}
						icon="search"
						value={searchTerm}
						onChange={(e: any) => setSearchTerm(e.target.value)}
					/>
				</div>

				{/* The one flexible column. Pills stay on a single row and scroll
				    sideways when they don't fit, so the toolbar never grows a second
				    line and the actions stay pinned right. */}
				<div className="relative min-w-0 flex-1">
					{/* The vertical padding (and its matching negative margin) keeps the
					    active pill's outline from being clipped by the scroll container
					    without changing the row's height. */}
					<div
						ref={pillScrollRef}
						onScroll={updateFade}
						className="-my-1 flex items-center gap-2 overflow-x-auto overflow-y-hidden py-1 [&>*]:shrink-0"
					>
						{orgFilters.map((f) => (
							<Tab
								key={f.id}
								action={() => applyDefinition(f.definition)}
								active={isOrgFilterActive(f)}
								text={f.name}
								role="filter"
							/>
						))}

						{savedFilters.map((saved) => (
							<Tab
								key={saved.id}
								action={() => selectSavedFilter(saved)}
								active={isSavedFilterActive(saved)}
								text={saved.name}
								role="filter"
							/>
						))}
					</div>

					{/* Softens the cut where pills run past the edge. Fades to the page
					    background (#f2f2f2, set on LoggedInLayout); the far stop is that
					    same colour at zero alpha so the ramp never darkens mid-way. */}
					{fade.start && (
						<div
							aria-hidden
							className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#f2f2f2] to-[#f2f2f200]"
						/>
					)}
					{fade.end && (
						<div
							aria-hidden
							className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#f2f2f2] to-[#f2f2f200]"
						/>
					)}
				</div>

				<div className="ml-auto flex shrink-0 items-center gap-2">
					{menuActions && menuActions.length > 0 && <ListActionsMenu actions={menuActions} />}

					{columns && <ColumnSettingsMenu columns={columns} />}

					{hasActiveFilter ? (
						<div className="flex h-[33px] items-center gap-2 rounded-md border border-solid border-border bg-white pl-2.5 pr-2">
							<button
								onClick={onOpenFilterPanel}
								className="flex items-center gap-2"
								title={t("FILTER" as any)}
							>
								<AppIcon icon="filter-thin" margin="0" />
								<span className="inline-flex items-center gap-1.5 rounded-full bg-foreground py-0.5 pl-1.5 pr-2 text-xs font-semibold text-white">
									<span className="h-2 w-2 rounded-full bg-action" />
									{count}
								</span>
							</button>
							<button
								onClick={clearFilter}
								className="flex items-center opacity-60 hover:opacity-100"
								title={t("CLEAR" as any)}
							>
								<AppIcon icon="cross" size="11px" margin="0" />
							</button>
						</div>
					) : (
						<button
							onClick={onOpenFilterPanel}
							className="flex h-[33px] w-10 items-center justify-center rounded-md border border-solid border-border bg-white hover:border-action"
							title={t("FILTER" as any)}
						>
							<AppIcon icon="filter-thin" margin="0" />
						</button>
					)}

					<Link to={createTo}>
						<CreateButton height="33px">{t(createLabel as any)}</CreateButton>
					</Link>
				</div>
			</div>
		</div>
	);
}

export default ListPageToolbar;
